import BreadCrumb from "Components/Common/BreadCrumb";
import { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Container,
  Nav,
  NavItem,
  TabContent,
  TabPane,
  NavLink,
} from "reactstrap";
import classnames from "classnames";
import { useFormik } from "formik";
import * as Yup from "yup";
import PaymentInfo from "Components/Settings/PaymentAndInvoice/PaymentInfo";
import BillingPreferences from "Components/Settings/PaymentAndInvoice/BillingPreferences";
import QuoteInvoiceSetting from "Components/Settings/PaymentAndInvoice/QuoteInvoiceSettings";
import { useAppDispatch, useAppSelector } from "Components/Hooks/hooks";
import {
  billingPreferences,
  getBillingDetails,
  manageBillingDetails,
  updateQuoteInvoiceSettings,
} from "slices/thunks";
import { selectPaymantsInvoicingState } from "slices/paymantsInvoicing/reducer";
import { createSelector } from "reselect";
import { useSelector } from "react-redux";
import Loader from "Components/Common/Loader";
import {
  VALIDATION_MESSAGES,
  VALIDATION_LIMITS,
} from "Components/constants/validation";
import { useLocation } from "react-router-dom";
import { updateUserPayload } from "slices/auth/reducer";

const paymentSchema = Yup.object().shape({
  paymentId: Yup.string()
    .email(VALIDATION_MESSAGES.INVALID_EMAIL)
    .required(VALIDATION_MESSAGES.REQUIRED("Payment ID")),

  bankName: Yup.string()
    .required(VALIDATION_MESSAGES.REQUIRED("Bank name"))
    .max(
      VALIDATION_LIMITS.BANK_NAME.MAX,
      VALIDATION_MESSAGES.MAX_LENGTH(
        "Bank name",
        VALIDATION_LIMITS.BANK_NAME.MAX,
      ),
    ),

  accountName: Yup.string()
    .required(VALIDATION_MESSAGES.REQUIRED("Account name"))
    .max(
      VALIDATION_LIMITS.ACCOUNT_NAME.MAX,
      VALIDATION_MESSAGES.MAX_LENGTH(
        "Account name",
        VALIDATION_LIMITS.ACCOUNT_NAME.MAX,
      ),
    ),

  accountNumber: Yup.string()
    .required(VALIDATION_MESSAGES.REQUIRED("Account number"))
    .matches(/^[0-9]+$/, "Account number must contain only digits")
    .max(
      VALIDATION_LIMITS.ACCOUNT_NUMBER.MAX,
      VALIDATION_MESSAGES.MAX_LENGTH(
        "Account number",
        VALIDATION_LIMITS.ACCOUNT_NUMBER.MAX,
      ),
    ),

  sortCode: Yup.string()
    .required(VALIDATION_MESSAGES.REQUIRED("Sort code"))
    .max(
      VALIDATION_LIMITS.SORT_CODE.MAX,
      VALIDATION_MESSAGES.MAX_LENGTH(
        "Sort code",
        VALIDATION_LIMITS.SORT_CODE.MAX,
      ),
    ),
});

const billingPreferencesValidationSchema = Yup.object().shape({
  vatRate: Yup.string()
    .required(VALIDATION_MESSAGES.REQUIRED("VAT rate"))
    .max(
      VALIDATION_LIMITS.BILLING_PREFERENCES.VAT_RATE_MAX,
      VALIDATION_MESSAGES.MAX_LENGTH(
        "VAT rate",
        VALIDATION_LIMITS.BILLING_PREFERENCES.VAT_RATE_MAX,
      ),
    ),
  quoteExpiry: Yup.string()
    .required(VALIDATION_MESSAGES.REQUIRED("Quote expiry"))
    .matches(
      /^\d+$/,
      "Quote expiry must be a whole number (no decimals allowed)",
    )
    .max(
      VALIDATION_LIMITS.BILLING_PREFERENCES.QUOTE_EXPIRY_MAX,
      VALIDATION_MESSAGES.MAX_LENGTH(
        "Quote expiry",
        VALIDATION_LIMITS.BILLING_PREFERENCES.QUOTE_EXPIRY_MAX,
      ),
    ),
  paymentTerms: Yup.string()
    .required(VALIDATION_MESSAGES.REQUIRED("Payment terms"))
    .matches(
      /^\d+$/,
      "Payment terms must be a whole number (no decimals allowed)",
    )
    .max(
      VALIDATION_LIMITS.BILLING_PREFERENCES.PAYMENT_TERMS_MAX,
      VALIDATION_MESSAGES.MAX_LENGTH(
        "Payment terms",
        VALIDATION_LIMITS.BILLING_PREFERENCES.PAYMENT_TERMS_MAX,
      ),
    ),
});

const quoteInvoiceValidationSchema = Yup.object().shape({
  tnc: Yup.string()
    .trim()
    .required(VALIDATION_MESSAGES.REQUIRED("Terms and conditions"))
    .max(
      VALIDATION_LIMITS.QUOTE_TNC_MAX,
      VALIDATION_MESSAGES.MAX_LENGTH(
        "Terms and conditions",
        VALIDATION_LIMITS.QUOTE_TNC_MAX,
      ),
    ),
  message: Yup.string()
    .trim()
    .required(VALIDATION_MESSAGES.REQUIRED("Footer message"))
    .max(
      VALIDATION_LIMITS.FOOTER_MESSAGE_MAX,
      VALIDATION_MESSAGES.MAX_LENGTH(
        "Footer message",
        VALIDATION_LIMITS.FOOTER_MESSAGE_MAX,
      ),
    ),
  // signature: Yup.string().trim().required("Signature is required"),
});

export interface PaymentValues {
  paymentId: string;
  bankName: string;
  accountName: string;
  accountNumber: string;
  sortCode: string;
}

export interface BillingPreferencesValues {
  vatRate: number;
  quoteExpiry: number;
  paymentTerms: number;
}

export interface QuoteInvoiceValues {
  tnc: string;
  message: string;
  signature?: string;
}

const PaymentsAndInvoicing = () => {
  const dispatch = useAppDispatch();
  const configData = useAppSelector((stat) => stat.config.configData);
  const location = useLocation();
  const { signatureMissing } = location.state || {};
  const [activeTab, setActiveTab] = useState<string>(
    signatureMissing ? "3" : "1",
  );
  const [loading, setLoading] = useState<boolean>(false);

  const billingDataSelector = createSelector(
    selectPaymantsInvoicingState,
    (state) => ({
      billingData: state.billingData,
      error: state.errors,
      isLoading: state.status.fetch,
    }),
  );

  const { billingData, error, isLoading } = useSelector(billingDataSelector);

  const dataURLtoFile = (dataurl: string, filename: string) => {
    const arr = dataurl.split(",");
    const mime = arr[0].match(/:(.*?);/)![1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) u8arr[n] = bstr.charCodeAt(n);
    return new File([u8arr], filename, { type: mime });
  };

  useEffect(() => {
    dispatch(getBillingDetails());
  }, [dispatch]);

  const paymentValidation = useFormik<PaymentValues>({
    enableReinitialize: true,
    initialValues: {
      paymentId: billingData?.email ?? "",
      bankName: billingData?.bank_name ?? "",
      accountName: billingData?.name ?? "",
      accountNumber: billingData?.account_number ?? "",
      sortCode: billingData?.sort_code ?? "",
    },
    validationSchema: paymentSchema,
    onSubmit: async (values) => {
      console.log("Submitted Values:", values);
      try {
        setLoading(true);
        await dispatch(
          manageBillingDetails({
            name: values.accountName?.trim(),
            email: values.paymentId?.trim(),
            sort_code: values.sortCode?.trim(),
            account_number: String(values.accountNumber)?.trim(),
            bank_name: values.bankName?.trim(),
          }),
        )?.unwrap();
        dispatch(updateUserPayload({ hasBankAccountDetailAdded: true }));
        setActiveTab("2");
      } catch (error) {
        console.error("Error managing billing details:", error);
      } finally {
        setLoading(false);
      }
    },
  });

  const billingPreferencesValidation = useFormik<BillingPreferencesValues>({
    enableReinitialize: true,
    initialValues: {
      vatRate: configData?.billing_preferences?.vat?.id ?? 0,
      quoteExpiry: configData?.billing_preferences.quote_expiration ?? 0,
      paymentTerms: configData?.billing_preferences.payment_expiration ?? 0,
    },
    validationSchema: billingPreferencesValidationSchema,
    onSubmit: async (values) => {
      console.log("Submitted Values:", values);
      try {
        setLoading(true);
        await dispatch(
          billingPreferences({
            vat_id: values.vatRate,
            quote_expiration: values.quoteExpiry,
            payment_expiration: values.paymentTerms,
          }),
        )?.unwrap();
        setActiveTab("3");
      } catch (error) {
        console.log("Error updating billing preferences:", error);
      } finally {
        setLoading(false);
      }
    },
  });

  const quoteInvoiceValidation = useFormik<QuoteInvoiceValues>({
    enableReinitialize: true,
    initialValues: {
      tnc: configData?.quote_invoice_settings?.terms_and_conditions ?? "",
      message: configData?.quote_invoice_settings?.footer_message ?? "",
      signature: configData?.quote_invoice_settings?.signature ?? "",
    },
    validationSchema: quoteInvoiceValidationSchema,
    onSubmit: async (values) => {
      console.log("Submitted Quote/Invoice Values:", values);

      // Only convert signature to file if it exists and is a data URL
      let signatureFile = null;
      if (values.signature && values.signature.startsWith("data:")) {
        try {
          signatureFile = dataURLtoFile(values.signature, "signature.png");
        } catch (error) {
          console.error("Error converting signature:", error);
        }
      }

      try {
        setLoading(true);
        await dispatch(
          updateQuoteInvoiceSettings({
            terms_and_conditions: values.tnc?.trim(),
            footer_message: values.message?.trim(),
            signature: signatureFile,
          }),
        )?.unwrap();

        if (signatureFile) {
          dispatch(updateUserPayload({ hasSignatureAdded: true }));
        }
      } catch (error) {
        console.error("Error updating quote/invoice settings:", error);
      } finally {
        setLoading(false);
      }
    },
  });

  const defaultDummyOptions = configData?.vat_settings.map((vat) => {
    return {
      label: vat.name,
      value: vat.id,
    };
  }) as {
    label: string;
    value: number;
  }[];

  document.title = "Payments & Invoicing | EaziQuote";

  if (isLoading === "loading") {
    return (
      <Container
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "100vh" }}
      >
        <Loader error={error} />
      </Container>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title="Payments & Invoicing"
          pageTitle="Settings"
          url="/settings"
        />

        <Card className="bg-white br-10">
          <CardHeader
            className="bg-white m-0"
            style={{ borderTopLeftRadius: 10, borderTopRightRadius: 10 }}
          >
            <Nav
              className="nav-tabs-custom rounded card-header-tabs border-bottom-0 "
              role="tablist"
            >
              <NavItem>
                <NavLink
                  to="#"
                  onClick={() => setActiveTab("1")}
                  className={classnames({ active: activeTab === "1" })}
                  style={{
                    fontSize: 16,
                    fontWeight: activeTab === "1" ? 500 : 400,
                    fontFamily: "Inter Tight",
                    color: activeTab === "1" ? "#082B60" : "#2D2D2D",
                  }}
                >
                  Payment Info
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  to="#"
                  onClick={() => setActiveTab("2")}
                  className={classnames({ active: activeTab === "2" })}
                  style={{
                    fontSize: 16,
                    fontWeight: activeTab === "2" ? 500 : 400,
                    fontFamily: "Inter Tight",
                    color: activeTab === "2" ? "#082B60" : "#2D2D2D",
                  }}
                >
                  Billing Preferences
                </NavLink>
              </NavItem>

              <NavItem>
                <NavLink
                  to="#"
                  onClick={() => setActiveTab("3")}
                  className={classnames({ active: activeTab === "3" })}
                  style={{
                    fontSize: 16,
                    fontWeight: activeTab === "3" ? 500 : 400,
                    fontFamily: "Inter Tight",
                    color: activeTab === "3" ? "#082B60" : "#2D2D2D",
                  }}
                >
                  Quote & Invoice Settings
                </NavLink>
              </NavItem>
            </Nav>
          </CardHeader>

          <CardBody className="p-4 mt-0 pt-0">
            <TabContent activeTab={activeTab}>
              <TabPane tabId="1">
                <PaymentInfo validation={paymentValidation} loading={loading} />
              </TabPane>

              <TabPane tabId="2">
                <BillingPreferences
                  validation={billingPreferencesValidation}
                  rateOptions={defaultDummyOptions}
                  loading={loading}
                />
              </TabPane>

              <TabPane tabId="3">
                <QuoteInvoiceSetting
                  validation={quoteInvoiceValidation}
                  navigateToSignature={signatureMissing}
                  loading={loading}
                />
              </TabPane>
            </TabContent>
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default PaymentsAndInvoicing;
